/**
 * Cadence Drill — free-practice mode.
 *
 * Hear a chord progression and identify the cadence type.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import {
  buildCadenceExerciseRound,
  DEFAULT_CADENCE_EXERCISE_CONFIG,
  cadenceDrillItemId,
  cadenceDrillStatsKey,
  CADENCE_TYPE_POOL,
  CADENCE_TYPES_ORDERED,
  type CadenceExerciseConfig,
  type CadenceExerciseRound,
  type CadenceType,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDrillStats,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { getChallengeData, recordChallengeAttempt } from "@music-app/persistence";

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const TONIC_OPTIONS: { label: string; midi: number }[] = [
  { label: "C",  midi: 60 }, { label: "G",  midi: 67 },
  { label: "D",  midi: 62 }, { label: "A",  midi: 69 },
  { label: "E",  midi: 64 }, { label: "F",  midi: 65 },
  { label: "B♭", midi: 70 }, { label: "E♭", midi: 63 },
  { label: "A♭", midi: 68 }, { label: "D♭", midi: 61 },
];

// ─── State machine ────────────────────────────────────────────────────────────

type CadenceExercisePhase = "config" | "playing" | "awaiting" | "reveal" | "graded" | "done";

interface SessionStat {
  correct: number;
  played: number;
}

interface CadenceExerciseState {
  phase: CadenceExercisePhase;
  config: CadenceExerciseConfig;
  sessionLength: number;
  round: CadenceExerciseRound | null;
  roundNumber: number;
  correctCount: number;
  selectedCadence: CadenceType | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<CadenceType, SessionStat>>;
}

type CadenceExerciseAction =
  | { type: "START"; config: CadenceExerciseConfig; sessionLength: number; round: CadenceExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; cadence: CadenceType }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: CadenceExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: CadenceExerciseState = {
  phase: "config",
  config: DEFAULT_CADENCE_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedCadence: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function addStat(
  stats: Partial<Record<CadenceType, SessionStat>>,
  type: CadenceType,
  correct: boolean | null,
): Partial<Record<CadenceType, SessionStat>> {
  const prev = stats[type] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [type]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function cadenceDrillReducer(state: CadenceExerciseState, action: CadenceExerciseAction): CadenceExerciseState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addStat(state.sessionStats, state.round.cadenceType, null),
        };
      }
      return { ...state, phase: "awaiting" };
    }

    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = action.cadence === state.round.cadenceType;
      return {
        ...state,
        phase: "graded",
        selectedCadence: action.cadence,
        wasCorrect: correct,
        wasTimeout: false,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addStat(state.sessionStats, state.round.cadenceType, correct),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addStat(state.sessionStats, state.round.cadenceType, false),
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedCadence: null,
        wasCorrect: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CadenceExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(cadenceDrillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("cadences", listenMode, nodeId);
  const { recordItem } = useExerciseSession("cadences", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);

  // Unused — satisfies the useState pattern for consistency with other drills.
  void audioPlayer;

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    setAudioPlayer(player);
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Challenge mode: load previous best on mount.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("cadences").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Record response start time when awaiting answer (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  // FSRS write + review record after grading (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = cadenceDrillItemId(state.round.cadenceType, state.round.contextLength);
    const correct = state.wasCorrect === true;
    const responseMs = responseStartRef.current
      ? Date.now() - responseStartRef.current
      : 8000;
    const chosenAnswerId =
      !correct && state.selectedCadence !== null ? state.selectedCadence : null;
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) {
        challengeCorrectRef.current += 1;
        const rating: Rating = responseMs < 5000 ? "good" : "hard";
        void applyReview(itemId, rating);
      } else {
        void applyReview(itemId, "again");
      }
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedCadence, state.config.fsrsEnabled, state.config.drillMode, challengeMode, applyReview]);

  // Drill stats write after graded answer (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      cadenceDrillStatsKey(state.round.cadenceType, state.round.contextLength, state.config),
      "graded",
      state.wasCorrect ?? false,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Drill stats write after listening-mode reveal (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(
      cadenceDrillStatsKey(state.round.cadenceType, state.round.contextLength, state.config),
      "listening",
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(cadenceDrillItemId(state.round.cadenceType, state.round.contextLength));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Challenge mode: record attempt on session done.
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const correct = challengeCorrectRef.current;
    void recordChallengeAttempt({
      challengeKey: "cadences",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Challenge mode: auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const id = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  const playRound = useCallback(
    async (round: CadenceExerciseRound, drillMode: CadenceExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);

      if (drillMode === "listening") {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
        await player.play(round.teachAudio);
      }

      recordItem(cadenceDrillItemId(round.cadenceType, round.contextLength));
      dispatch({ type: "PLAYBACK_DONE" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordItem],
  );

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const startDrill = useCallback(
    (config: CadenceExerciseConfig, sessionLength: number) => {
      const round = buildCadenceExerciseRound(config, nextRoundSeed());
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildCadenceExerciseRound(state.config, nextRoundSeed());
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound]);

  const handleSelect = useCallback(
    (cadence: CadenceType) => {
      if (!state.round || state.phase !== "awaiting") return;
      const correct = cadence === state.round.cadenceType;

      dispatch({ type: "SELECT", cadence });

      if (uiSoundsEnabled) {
        if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
        uiSoundRef.current.play(correct ? "correct" : "wrong");
      }

      if (!correct && state.round.teachAudio) {
        const teachPlan = state.round.teachAudio;
        const player = playerRef.current;
        if (player) {
          window.setTimeout(() => { void player.play(teachPlan); }, 600);
        }
      }
    },
    [state.round, state.phase, uiSoundsEnabled],
  );

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if (state.phase === "graded" || state.phase === "reveal") {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextRound();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, nextRound, replay]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <CadenceExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <CadenceExerciseCoachStart itemCount={coachItemCount} onStart={startDrill} />;
    return <CadenceExerciseConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <CadenceExerciseSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <CadenceExerciseLoop
      state={state}
      listenMode={listenMode}
      practiceMode={practiceMode}
      challengeMode={challengeMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onNext={nextRound}
      onReplay={replay}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Coached listen auto-start ───────────────────────────────────────────────

function CadenceExerciseCoachStart({
  itemCount,
  onStart,
}: {
  itemCount: number;
  onStart: (config: CadenceExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CADENCE_EXERCISE_CONFIG, drillMode: "listening", fsrsEnabled: false }, itemCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function CadenceExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: CadenceExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CADENCE_EXERCISE_CONFIG, drillMode: "graded", fsrsEnabled: false }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Listen mode auto-start ───────────────────────────────────────────────────

// ─── Config persistence ───────────────────────────────────────────────────────

const CADENCE_DRILL_LS_KEY = "cadence-drill-config-v2";

interface CadenceExerciseSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  contextLength: CadenceExerciseConfig["contextLength"];
  tempo: CadenceExerciseConfig["tempo"];
  tonic: CadenceExerciseConfig["tonic"];
  fixedTonicMidi: number;
  selectedCadences: CadenceType[];
}

const CADENCE_DRILL_DEFAULTS: CadenceExerciseSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  contextLength: "full",
  tempo: "medium",
  tonic: "random",
  fixedTonicMidi: 60,
  selectedCadences: [...DEFAULT_CADENCE_EXERCISE_CONFIG.cadences],
};

function loadCadenceExerciseConfig(): CadenceExerciseSavedConfig {
  try {
    const raw = localStorage.getItem(CADENCE_DRILL_LS_KEY);
    if (raw) return { ...CADENCE_DRILL_DEFAULTS, ...(JSON.parse(raw) as Partial<CadenceExerciseSavedConfig>) };
  } catch { /* ignore */ }
  return CADENCE_DRILL_DEFAULTS;
}

function saveCadenceExerciseConfig(cfg: CadenceExerciseSavedConfig): void {
  try { localStorage.setItem(CADENCE_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Config screen ────────────────────────────────────────────────────────────

interface CadenceExerciseConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: CadenceExerciseConfig, sessionLength: number) => void;
}

function CadenceExerciseConfigScreen({ mode, onStart }: CadenceExerciseConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const [initConfig] = useState(loadCadenceExerciseConfig);

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );
  const modeColor = PICKER_MODE_COLOR[mode];
  const [contextLength, setContextLength] = useState<CadenceExerciseConfig["contextLength"]>(initConfig.contextLength);
  const [tempo, setTempo] = useState<CadenceExerciseConfig["tempo"]>(initConfig.tempo);
  const [tonic, setTonic] = useState<CadenceExerciseConfig["tonic"]>(initConfig.tonic);
  const [fixedTonicMidi, setFixedTonicMidi] = useState(initConfig.fixedTonicMidi);
  const [selectedCadences, setSelectedCadences] = useState<Set<CadenceType>>(
    () => new Set(initConfig.selectedCadences),
  );

  const [historyStats, setHistoryStats] = useState<Partial<Record<string, DrillStatsRecord | null>>>({});

  // Load history stats for checked cadences.
  useEffect(() => {
    const cadences = Array.from(selectedCadences);
    const cfgSnap = { tonic, fixedTonicMidi };
    void Promise.all(
      cadences.map((c) => {
        const key = cadenceDrillStatsKey(c, contextLength, cfgSnap);
        return getDrillStats(key).then((s) => [key, s ?? null] as const);
      }),
    ).then((entries) => setHistoryStats(Object.fromEntries(entries)));
  }, [selectedCadences, contextLength, tonic, fixedTonicMidi]);

  // Persist config on change.
  useEffect(() => {
    saveCadenceExerciseConfig({
      sessionLength, mode, contextLength, tempo, tonic, fixedTonicMidi,
      selectedCadences: Array.from(selectedCadences),
    });
  }, [sessionLength, mode, contextLength, tempo, tonic, fixedTonicMidi, selectedCadences]);

  const toggleCadence = (type: CadenceType) => {
    setSelectedCadences((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size <= 2) return prev; // minimum 2 cadences
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleStart = () => {
    if (selectedCadences.size < 2) return;
    onStart(
      {
        cadences: CADENCE_TYPES_ORDERED.filter((t) => selectedCadences.has(t)),
        contextLength,
        tempo,
        tonic,
        fixedTonicMidi,
        drillMode: mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice",
      },
      sessionLength,
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Cadence Recognition"
        subhead="Hear a chord progression — identify the cadence type."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/cadences" />
      <ExerciseLessonLink nodeIds={["cadence-recognition"]} />

      {/* Session settings */}
      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={!showCustomLength && sessionLength === n}
                onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                className={`px-4 py-2 text-sm border transition-colors ${
                  !showCustomLength && sessionLength === n
                    ? ""
                    : "bg-paper text-ink border-ink/20 hover:border-ink"
                }`}
                style={!showCustomLength && sessionLength === n ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength
                  ? ""
                  : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        <ConfigRow label="Context">
          <NativeSelect
            value={contextLength}
            onChange={(v) => setContextLength(v as CadenceExerciseConfig["contextLength"])}
            options={[
              { value: "full",  label: "Full  (4-chord progression)" },
              { value: "short", label: "Short  (final 2 chords only)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Tempo">
          <NativeSelect
            value={tempo}
            onChange={(v) => setTempo(v as CadenceExerciseConfig["tempo"])}
            options={[
              { value: "slow",   label: "Slow" },
              { value: "medium", label: "Medium" },
              { value: "fast",   label: "Fast" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Key">
          <div className="flex gap-3 flex-wrap items-center">
            <NativeSelect
              value={tonic}
              onChange={(v) => setTonic(v as CadenceExerciseConfig["tonic"])}
              options={[
                { value: "random", label: "Random key" },
                { value: "fixed",  label: "Fixed key" },
              ]}
            />
            {tonic === "fixed" && (
              <NativeSelect
                value={String(fixedTonicMidi)}
                onChange={(v) => setFixedTonicMidi(Number(v))}
                options={TONIC_OPTIONS.map((t) => ({ value: String(t.midi), label: t.label }))}
              />
            )}
          </div>
        </ConfigRow>
      </div>

      {/* Cadence pool */}
      <div className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Cadence pool <span className="text-inkFaded/60">(minimum 2)</span>
        </p>

        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSelectedCadences(new Set(CADENCE_TYPES_ORDERED))}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setSelectedCadences(new Set(["PAC", "plagal", "half", "deceptive"] as CadenceType[]))}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
          >
            Common 4
          </button>
        </div>

        <div className="border border-ink/10">
          {CADENCE_TYPES_ORDERED.map((type) => {
            const checked = selectedCadences.has(type);
            const info = CADENCE_TYPE_POOL[type];
            const wouldViolateMin = checked && selectedCadences.size <= 2;
            return (
              <label
                key={type}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-ink/10 last:border-b-0 ${
                  checked ? "text-ink" : "text-inkFaded"
                } ${wouldViolateMin ? "cursor-not-allowed" : "hover:bg-ink/5"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCadence(type)}
                  disabled={wouldViolateMin}
                  className="accent-ink w-3.5 h-3.5 shrink-0"
                  aria-label={`Include ${info.longName}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-sm font-medium">{info.longName}</span>
                    <span className="font-mono text-[10px] text-inkFaded">{info.shortLabel}</span>
                  </div>
                  <span className="font-mono text-[10px] text-inkFaded/70">{info.description}</span>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* History table */}
      <CadenceHistoryTable
        cadences={CADENCE_TYPES_ORDERED.filter((t) => selectedCadences.has(t))}
        contextLength={contextLength}
        tonicConfig={{ tonic, fixedTonicMidi }}
        historyStats={historyStats}
      />

      <button
        type="button"
        onClick={handleStart}
        disabled={selectedCadences.size < 2}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface CadenceExerciseLoopProps {
  state: CadenceExerciseState;
  listenMode: boolean;
  practiceMode: boolean;
  challengeMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (cadence: CadenceType) => void;
  onNext: () => void;
  onReplay: () => void;
  onStop: () => void;
  from?: string;
}

function CadenceExerciseLoop({
  state,
  listenMode,
  practiceMode,
  challengeMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onNext,
  onReplay,
  onStop,
  from,
}: CadenceExerciseLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, selectedCadence, wasCorrect, wasTimeout } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isReveal   = phase === "reveal";
  const isGraded   = phase === "graded";

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  const cadenceButtons = CADENCE_TYPES_ORDERED.filter((t) => config.cadences.includes(t));

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Cadence Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {config.contextLength === "full" ? "4-chord progression" : "2-chord context"}
        {" · "}
        {config.tempo}
        {" · "}
        {config.tonic === "random" ? "random key" : `fixed key`}
      </p>

      {/* Progress bar */}
      <div className="w-full h-0.5 bg-ink/10 mb-2" role="progressbar" aria-valuenow={roundNumber} aria-valuemin={1} aria-valuemax={sessionLength}>
        <div className="h-full transition-all duration-300" style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }} />
      </div>

      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        {isPlaying && <span className="animate-pulse">Playing…</span>}
      </div>

      {/* Key info */}
      {round && (
        <div className="mb-4 font-mono text-xs text-inkFaded">
          Key: <span className="text-ink">{round.keyName}</span>
        </div>
      )}

      {/* Chord progression labels — shown on reveal/graded */}
      <div className="mb-6 min-h-[3.5rem]">
        {(isReveal || isGraded) && round ? (
          <div className="flex items-center gap-2 flex-wrap" aria-label="Chord progression">
            {round.romanLabels.map((label, i) => (
              <span
                key={i}
                className="font-mono text-sm px-2.5 py-1 border"
                style={i === round.romanLabels.length - 1
                  ? { borderColor: modeColor, color: modeColor, backgroundColor: `${modeColor}18` }
                  : { borderColor: "rgba(26,24,19,0.2)", color: "var(--color-inkSoft)" }}
              >
                {label}
              </span>
            ))}
          </div>
        ) : isPlaying ? (
          <p className="font-serif italic text-inkFaded">
            {config.contextLength === "full" ? "4 chords playing…" : "2 chords playing…"}
          </p>
        ) : null}
      </div>

      {/* Prompt */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            What cadence did you hear?
          </p>
        )}
        {(isReveal || isGraded) && round && (
          <div aria-live="polite">
            {isReveal && (
              <p className="font-serif italic text-xl text-ink">
                That was{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CADENCE_TYPE_POOL[round.cadenceType].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                Correct —{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CADENCE_TYPE_POOL[round.cadenceType].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && !wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                {wasTimeout ? "Time! " : "The answer was "}{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {CADENCE_TYPE_POOL[round.cadenceType].longName}
                </strong>
                .
              </p>
            )}
            {isGraded && !challengeMode && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1"
                 style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Answer buttons */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-2 mb-2">
          {cadenceButtons.map((type) => {
            const info = CADENCE_TYPE_POOL[type];
            const isCorrect  = type === round?.cadenceType;
            const isSelected = type === selectedCadence;
            const showCorrect = (isGraded || isReveal) && isCorrect;
            const showWrong   = isGraded && isSelected && !isCorrect;
            const disabled    = isPlaying || isReveal || isGraded;

            return (
              <button
                key={type}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(type)}
                aria-pressed={isSelected && isAwaiting}
                aria-label={`${info.longName} cadence`}
                className={`px-3 py-3 border text-left transition-colors ${
                  showCorrect
                    ? "bg-success/10 border-success text-success"
                    : showWrong
                    ? "bg-danger/10 border-danger text-danger"
                    : disabled
                    ? "bg-paper border-ink/10 text-inkFaded"
                    : "bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5"
                }`}
              >
                <div className="font-serif text-sm font-medium leading-tight">{info.longName}</div>
                <div className="font-mono text-[10px] text-inkFaded mt-0.5">{info.description}</div>
              </button>
            );
          })}
        </div>
        {isAwaiting && <p className="mt-2 font-mono text-[10px] text-inkFaded">R to replay</p>}
        {(isReveal || isGraded) && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the progression"
          >
            ♩ Replay
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface CadenceExerciseSummaryProps {
  config: CadenceExerciseConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<CadenceType, SessionStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function CadenceExerciseSummary({
  config,
  sessionLength,
  correctCount,
  sessionStats,
  onAgain,
  onStop,
}: CadenceExerciseSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="cadence"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Correct</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}<span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {Object.keys(sessionStats).length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">By cadence</p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Cadence</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>}
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>}
            </div>
            {CADENCE_TYPES_ORDERED.filter((t) => sessionStats[t] !== undefined).map((type) => {
              const stat = sessionStats[type]!;
              const pct = stat.played > 0 && stat.correct > 0
                ? Math.round((stat.correct / stat.played) * 100)
                : stat.played > 0 ? 0 : null;
              const info = CADENCE_TYPE_POOL[type];
              return (
                <div key={type} className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
                  <div>
                    <span className="font-serif text-sm text-ink">{info.longName}</span>
                    <span className="font-mono text-xs text-inkFaded ml-2">{info.shortLabel}</span>
                  </div>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button type="button" onClick={onAgain} className={buttonClasses("primary")}>
          Drill again →
        </button>
        <button type="button" onClick={onStop} className={buttonClasses("secondary")}>
          Change settings
        </button>
        <Link to="/exercise" className={buttonClasses("secondary")}>
          All drills
        </Link>
        <Link to="/stats" className={buttonClasses("secondary")}>
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["cadence-recognition"]} />
      </div>
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

interface CadenceHistoryTableProps {
  cadences: readonly CadenceType[];
  contextLength: CadenceExerciseConfig["contextLength"];
  tonicConfig: Pick<CadenceExerciseConfig, "tonic" | "fixedTonicMidi">;
  historyStats: Partial<Record<string, DrillStatsRecord | null>>;
}

function CadenceHistoryTable({ cadences, contextLength, tonicConfig, historyStats }: CadenceHistoryTableProps): ReactNode {
  const rows = cadences.map((c) => ({
    type: c,
    stats: historyStats[cadenceDrillStatsKey(c, contextLength, tonicConfig)] ?? null,
  }));

  const hasAnyData = rows.some((r) => r.stats && r.stats.played > 0);
  const [open, setOpen] = useState(false);
  if (!hasAnyData) return null;
  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      <div className={open ? "border border-ink/10" : "hidden"}>
        <div className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2 border-b border-ink/10">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Cadence</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
        </div>
        {rows
          .filter((r) => r.stats && r.stats.played > 0)
          .map(({ type, stats }) => {
            const s = stats!;
            const listened = Math.max(0, s.played - s.correct - s.errors);
            const graded   = s.correct + s.errors;
            const pct      = graded > 0 ? Math.round((s.correct / graded) * 100) : null;
            const info = CADENCE_TYPE_POOL[type];
            return (
              <div key={type} className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
                <div>
                  <span className="font-serif text-sm text-ink">{info.longName}</span>
                  <span className="font-mono text-xs text-inkFaded ml-2">{info.shortLabel}</span>
                </div>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{listened}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{graded}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{graded > 0 ? s.correct : "—"}</span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{pct !== null ? `${pct}%` : "—"}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">{label}</p>
      {children}
    </div>
  );
}

function NativeSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">▾</span>
    </div>
  );
}
